/**
 * Phase C — Tilt parallax hook.
 *
 * Returns a smoothed (x, y) offset clamped to [-1, 1] driven by:
 *   - device orientation (gamma/beta) on mobile when available
 *   - mouse position on desktop
 *   - touch fallback on iOS where DeviceOrientation needs a permission gesture
 *
 * Performance:
 *   - rAF loop is throttled to ~30fps on coarse pointers (mobile), ~60fps on desktop.
 *   - Input events are throttled to ~60Hz max so a high-rate trackpad/gyro
 *     can't flood the target ref.
 *   - Updates short-circuit when the smoothed delta is below a tiny epsilon —
 *     no React re-render, no transform churn, no GPU work.
 *   - Pauses the rAF loop entirely while the tab is hidden.
 *
 * Honors `prefers-reduced-motion` (returns zero offset) and is tolerant of
 * permission-gated DeviceOrientation APIs (iOS) — falls back silently.
 */

import { useEffect, useRef, useState } from 'react';

interface TiltOffset {
  x: number;
  y: number;
}

interface UseTiltParallaxOptions {
  /** Multiplier applied to the raw offset (default 1). */
  intensity?: number;
  /** Disable entirely (e.g. for reduced motion or static export). */
  disabled?: boolean;
}

const ZERO: TiltOffset = { x: 0, y: 0 };
/** Smoothed delta below this is treated as no-change. */
const EPSILON = 0.0015;
/** Min ms between input samples (≈60Hz). */
const INPUT_THROTTLE_MS = 16;

const clamp1 = (n: number) => (n > 1 ? 1 : n < -1 ? -1 : n);

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export function useTiltParallax(opts: UseTiltParallaxOptions = {}): TiltOffset {
  const { intensity = 1, disabled = false } = opts;
  const [offset, setOffset] = useState<TiltOffset>(ZERO);
  const target = useRef<TiltOffset>(ZERO);
  const current = useRef<TiltOffset>(ZERO);
  const lastInput = useRef(0);
  const lastFrame = useRef(0);
  const raf = useRef<number | null>(null);

  useEffect(() => {
    if (disabled || prefersReducedMotion() || typeof window === 'undefined') return;

    const isCoarse = window.matchMedia?.('(pointer: coarse)').matches;
    // Throttle frame updates harder on mobile/low-end devices.
    const FRAME_BUDGET_MS = isCoarse ? 33 : 16;

    const setTarget = (x: number, y: number) => {
      const now = performance.now();
      if (now - lastInput.current < INPUT_THROTTLE_MS) return;
      lastInput.current = now;
      target.current.x = clamp1(x) * intensity;
      target.current.y = clamp1(y) * intensity;
    };

    const handleMouse = (e: MouseEvent) => {
      const w = window.innerWidth || 1;
      const h = window.innerHeight || 1;
      setTarget((e.clientX / w) * 2 - 1, (e.clientY / h) * 2 - 1);
    };

    const handleOrientation = (e: DeviceOrientationEvent) => {
      // gamma: left/right [-90, 90], beta: front/back [-180, 180]
      setTarget((e.gamma ?? 0) / 30, (e.beta ?? 0) / 45);
    };

    let cleanupTouch: (() => void) | null = null;

    if (isCoarse) {
      const Anchor = (window as any).DeviceOrientationEvent;
      if (Anchor && typeof Anchor.requestPermission !== 'function') {
        window.addEventListener('deviceorientation', handleOrientation, { passive: true });
      } else {
        const handleTouch = (e: TouchEvent) => {
          const t = e.touches[0];
          if (!t) return;
          const w = window.innerWidth || 1;
          const h = window.innerHeight || 1;
          setTarget((t.clientX / w) * 2 - 1, (t.clientY / h) * 2 - 1);
        };
        window.addEventListener('touchmove', handleTouch, { passive: true });
        cleanupTouch = () => window.removeEventListener('touchmove', handleTouch);
      }
    } else {
      window.addEventListener('mousemove', handleMouse, { passive: true });
    }

    // Throttled rAF lerp — also skips React updates when delta is negligible.
    const tick = (now: number) => {
      raf.current = requestAnimationFrame(tick);
      if (now - lastFrame.current < FRAME_BUDGET_MS) return;
      lastFrame.current = now;

      const cur = current.current;
      const tgt = target.current;
      const nx = cur.x + (tgt.x - cur.x) * 0.06;
      const ny = cur.y + (tgt.y - cur.y) * 0.06;

      if (Math.abs(nx - cur.x) < EPSILON && Math.abs(ny - cur.y) < EPSILON) {
        return; // settled — skip render
      }
      cur.x = nx;
      cur.y = ny;
      setOffset({ x: nx, y: ny });
    };

    const handleVisibility = () => {
      if (document.hidden) {
        if (raf.current != null) cancelAnimationFrame(raf.current);
        raf.current = null;
      } else if (raf.current == null) {
        lastFrame.current = 0;
        raf.current = requestAnimationFrame(tick);
      }
    };

    raf.current = requestAnimationFrame(tick);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (raf.current != null) cancelAnimationFrame(raf.current);
      raf.current = null;
      window.removeEventListener('mousemove', handleMouse);
      window.removeEventListener('deviceorientation', handleOrientation);
      document.removeEventListener('visibilitychange', handleVisibility);
      cleanupTouch?.();
    };
  }, [disabled, intensity]);

  if (disabled) return ZERO;
  return offset;
}
